"use client";

import { useCart } from "@/context/CartContext";
import { useOffer } from "@/context/OfferContext";
import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { usePostHog } from "@/hooks/usePostHog";
import { getGAClientId } from "@/lib/ga";
import { isCampaignActive, getCampaignCode, campaignPercent } from "@/lib/campaign";
import { getCheckoutAttribution } from "@/lib/attribution";

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const STOREFRONT_HOST = process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN!;
// e.g. 'olinlabs.mx' or 'kbrpy0-zc.myshopify.com'

export default function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { cart, loading, addItem, updateItem, removeItem, clearCart, clearDiscounts, applyDiscount } = useCart();
  const { pendingDiscountCode } = useOffer();
  const [isAnimating, setIsAnimating] = useState(false);
  const [email, setEmail] = useState('');
  const [zip, setZip] = useState('');
  const [couponCode, setCouponCode] = useState(getCampaignCode() ?? '');
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [giftInfo, setGiftInfo] = useState<{gifts: string[], isFirstOrder: boolean, giftDetails?: {handle: string; name: string; title: string; imageUrl: string; variantId?: string}[]} | null>(null);
  const [giftPreviewIndex, setGiftPreviewIndex] = useState<number | null>(null);
  const [autoRedirect, setAutoRedirect] = useState<number | null>(null);
  const redirectUrlRef = useRef<string | null>(null);
  const redirectIntervalRef = useRef<any>(null);
  const { track, identify } = usePostHog();
  // Derived groups for rendering (must be above early returns to keep hooks order stable)
  const productLines = useMemo(() => (cart?.lines.edges || []).filter(({ node }) => parseFloat(node.cost.totalAmount.amount) > 0), [cart?.lines.edges]);
  const giftLines = useMemo(() => (cart?.lines.edges || []).filter(({ node }) => parseFloat(node.cost.totalAmount.amount) === 0), [cart?.lines.edges]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Cleanup interval on unmount (must be before any conditional return to keep hooks order stable)
  useEffect(() => {
    return () => {
      if (redirectIntervalRef.current) clearInterval(redirectIntervalRef.current);
    };
  }, []);

  const handleClose = () => {
    cancelAutoRedirect();
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300); // Match the transition duration
  };

  if (!isOpen) return null;

  const handleCheckout = async () => {
    if (!cart || cart.lines.edges.length === 0) return;

    // Show checkout form to capture email/zip
    setShowCheckoutForm(true);
    try {
      // GA4: add_shipping_info via mapper, PostHog: checkout_form_shown
      const items = cart.lines.edges.map(({ node }) => ({
        product_id: node.merchandise.product.id,
        product_title: node.merchandise.product.title,
        product_handle: node.merchandise.product.handle,
        variant_id: node.merchandise.id,
        variant_title: node.merchandise.title,
        quantity: node.quantity,
        price: parseFloat(node.merchandise.price.amount),
      }));
      track('checkout_form_shown', {
        cart_total: parseFloat(cart.cost.totalAmount.amount),
        cart_quantity: cart.totalQuantity,
        items_count: cart.lines.edges.length,
        currency: cart.cost.totalAmount.currencyCode,
        items,
      });
    } catch {}
  };


  // checkout is now handled by browser navigation to our API (no fetch preflight)

  // Cancel auto-redirect helper
  const cancelAutoRedirect = () => {
    if (redirectIntervalRef.current) clearInterval(redirectIntervalRef.current);
    setAutoRedirect(null);
  };

  

  const handleStartCheckout = async () => {
    if (!cart || cart.lines.edges.length === 0 || !email.trim()) return;

    // Identify user with provided email before checkout and merge anon history
    const e = email.trim();
    try { track('checkout_email_submitted', { email: e, zip: zip.trim() || undefined }); } catch {}
    identify(e, { source: 'checkout_email', zip: zip.trim() || undefined });
    try { (window as any).posthog?.alias?.(e) } catch {}

    // We've already shown gifts (if any). Now proceed to checkout
    setIsCheckingOut(true);

    try {
      // GA4: add_payment_info via mapper, PostHog: checkout_continue_clicked
      try {
        const items = cart.lines.edges.map(({ node }) => ({
          product_id: node.merchandise.product.id,
          product_title: node.merchandise.product.title,
          product_handle: node.merchandise.product.handle,
          variant_id: node.merchandise.id,
          variant_title: node.merchandise.title,
          quantity: node.quantity,
          price: parseFloat(node.merchandise.price.amount),
        }));
        track('checkout_continue_clicked', {
          cart_total: parseFloat(cart.cost.totalAmount.amount),
          cart_quantity: cart.totalQuantity,
          items_count: cart.lines.edges.length,
          currency: cart.cost.totalAmount.currencyCode,
          items,
        });
      } catch {}

      // Build API GET URL and navigate so browser follows 302
      // Get the main product (first non-free item)
      const mainProduct = cart.lines.edges.find(({ node }) => 
        parseFloat(node.cost.totalAmount.amount) > 0
      );

      if (!mainProduct) {
        throw new Error('No main product found in cart');
      }

      // One dedup ID shared by the browser pixel IC and the server CAPI IC —
      // without it Meta counts every checkout at least twice.
      const icEventId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `ic_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

      // Track checkout initiation (include line item snapshot for better analysis)
      track('checkout_initiated', {
        event_id: icEventId,
        cart_total: parseFloat(cart.cost.totalAmount.amount),
        cart_quantity: cart.totalQuantity,
        items_count: cart.lines.edges.length,
        currency: cart.cost.totalAmount.currencyCode,
        discount_code: couponCode.trim() || pendingDiscountCode || null,
        email: email.trim(),
        zip: zip.trim() || undefined,
        main_product: mainProduct.node.merchandise.product.title,
        main_product_handle: mainProduct.node.merchandise.product.handle,
        // For funnel breakdown compatibility, also include a normalized product_handle
        product_handle: mainProduct.node.merchandise.product.handle,
        items: cart.lines.edges.map(({ node }) => ({
          product_id: node.merchandise.product.id,
          product_title: node.merchandise.product.title,
          product_handle: node.merchandise.product.handle,
          variant_id: node.merchandise.id,
          variant_title: node.merchandise.title,
          quantity: node.quantity,
          price: parseFloat(node.merchandise.price.amount),
        }))
      });

      try {
        const u = new URL('/api/checkout/simple', window.location.origin);
        u.searchParams.set('variantId', mainProduct.node.merchandise.id);
        u.searchParams.set('qty', String(mainProduct.node.quantity || 1));
        u.searchParams.set('ic_eid', icEventId);
        const handle = mainProduct.node.merchandise.product?.handle;
        if (handle) u.searchParams.set('handle', handle);
        if (mainProduct.node.merchandise.product?.title) {
          u.searchParams.set('productTitle', mainProduct.node.merchandise.product.title);
        }
        if (email.trim()) u.searchParams.set('email', email.trim());
        if (couponCode.trim()) u.searchParams.set('discount', couponCode.trim());
        try {
          const gaCid = await getGAClientId().catch(() => undefined);
          if (gaCid) {
            u.searchParams.set('gaCid', gaCid);
            u.searchParams.set('ga', gaCid);
          }
        } catch {}
        const phDid = (window as any).posthog?.get_distinct_id?.();
        if (phDid) {
          u.searchParams.set('phDid', String(phDid));
          u.searchParams.set('ph', String(phDid));
        }
        // Forward UTM params + fbclid so Shopify cart attributes carry attribution.
        // Current URL wins; falls back to the last touch persisted at landing
        // (UTMs leave the URL as soon as the buyer navigates).
        try {
          for (const [k, v] of Object.entries(getCheckoutAttribution())) {
            if (v) u.searchParams.set(k, v);
          }
        } catch {}
        // Capture Meta _fbc / _fbp cookies for CAPI advanced matching
        try {
          const cookies = document.cookie.split('; ');
          const fbc = cookies.find(c => c.startsWith('_fbc='))?.split('=')[1];
          const fbp = cookies.find(c => c.startsWith('_fbp='))?.split('=')[1];
          if (fbc) u.searchParams.set('fbc', fbc);
          if (fbp) u.searchParams.set('fbp', fbp);
        } catch {}
        window.location.assign(u.toString());
        return;
      } catch (navErr) {
        console.error('Navigation error, falling back to direct assign', navErr);
        try {
          const u = new URL('/api/checkout/simple', window.location.origin);
          u.searchParams.set('variantId', mainProduct.node.merchandise.id);
          u.searchParams.set('qty', String(mainProduct.node.quantity || 1));
          u.searchParams.set('ic_eid', icEventId);
          const handle = mainProduct.node.merchandise.product?.handle;
          if (handle) u.searchParams.set('handle', handle);
          if (mainProduct.node.merchandise.product?.title) {
            u.searchParams.set('productTitle', mainProduct.node.merchandise.product.title);
          }
          if (email.trim()) u.searchParams.set('email', email.trim());
          if (couponCode.trim()) u.searchParams.set('discount', couponCode.trim());
          window.location.assign(u.toString());
          return;
        } catch {}
      }

    } catch (error) {
      console.error('Checkout error:', error);
      alert('Error starting checkout. Please try again.');
      setIsCheckingOut(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop - semi-transparent overlay over the website */}
      <div
        className={`absolute inset-0 transition-opacity duration-300 ease-out ${
          isAnimating ? 'bg-black/30' : 'bg-black/0'
        }`}
        onClick={handleClose}
      />
      
      {/* Drawer - slides in from the right */}
      <div className={`absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl rounded-bl-2xl rounded-tl-2xl transform transition-transform duration-300 ease-out ${
        isAnimating ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b p-4 md:py-3">
            <h2 className="text-2xl md:text-xl font-semibold">Tu Carrito</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-pulse text-center">
                <div className="h-4 bg-gray-200 rounded w-24 mx-auto mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-16 mx-auto"></div>
              </div>
            </div>
          ) : !cart || cart.lines.edges.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-center p-4">
              <div>
                <div className="text-gray-400 mb-2">
                  <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <p className="text-gray-500 text-base">Tu carrito está vacío</p>
                <button
                  onClick={handleClose}
                  className="mt-4 btn btn-primary"
                >
                  Seguir comprando
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-4 md:p-3 space-y-4 md:space-y-3">
                {/* Promo indicator — must name the code that is actually applied */}
                {isCampaignActive() && (
                  <div className="flex items-center gap-2 rounded-xl bg-green-50 border border-green-200 px-3 py-2">
                    <span className="text-green-700 text-sm font-mono font-bold">{getCampaignCode()}</span>
                    <span className="text-green-600 text-xs">{campaignPercent()}% de descuento aplicado</span>
                  </div>
                )}
                {/* Regular products */}
                {productLines.map(({ node: line }) => {
                  // Check if this item is a gift (free item with 0 price that matches gift handles)
                  const isGift = parseFloat(line.cost.totalAmount.amount) === 0 && (
                    line.merchandise.product.handle === 'olin-comb' ||
                    line.merchandise.product.handle === 'olin-bag' ||
                    line.merchandise.product.handle === 'olin-travel-case' /* deprecated gift - kept for legacy carts but not added anymore */
                  );
                  
                  return (
                  <div key={line.id} className="flex gap-4 border-b pb-4 md:pb-3">
                    <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                      <Image
                        src={line.merchandise.product.featuredImage.url}
                        alt={line.merchandise.product.featuredImage.altText || line.merchandise.product.title}
                        fill
                        className="object-cover"
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-lg md:text-base truncate">
                        {line.merchandise.product.title}
                      </h3>
                      {line.merchandise.title !== 'Default Title' && (
                        <p className="text-base md:text-sm text-gray-500 mt-1">
                          {line.merchandise.title}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between mt-2">
                        {isGift ? (
                          // Gift items - read only display
                          <div className="flex items-center gap-3">
                            <div className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-base bg-gray-50 text-gray-400 cursor-not-allowed">
                              −
                            </div>
                            <span className="text-lg md:text-base font-mono w-10 text-center text-gray-600">
                              {line.quantity}
                            </span>
                            <div className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-base bg-gray-50 text-gray-400 cursor-not-allowed">
                              +
                            </div>
                          </div>
                        ) : (
                          // Regular items - normal controls
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => {
                                if (line.quantity <= 1) {
                                  removeItem(line.id);
                                } else {
                                  updateItem(line.id, line.quantity - 1);
                                }
                              }}
                              className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-base hover:bg-gray-50 cursor-pointer"
                            >
                              −
                            </button>
                            <span className="text-lg md:text-base font-mono w-10 text-center">
                              {line.quantity}
                            </span>
                            <button
                              onClick={() => updateItem(line.id, line.quantity + 1)}
                              className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center text-base hover:bg-gray-50 cursor-pointer"
                            >
                              +
                            </button>
                          </div>
                        )}
                        
                        {!isGift && (
                          <button
                            onClick={() => removeItem(line.id)}
                            className="text-red-500 hover:text-red-700 text-xs"
                          >
                            Eliminar
                          </button>
                        )}
                        
                        {isGift && (
                          <span className="text-base md:text-sm text-black font-mono">
                            Incluido gratis
                          </span>
                        )}
                      </div>
                      
                      <p className="text-lg md:text-base font-semibold mt-1">
                        ${parseFloat(line.cost.totalAmount.amount).toFixed(2)} {line.cost.totalAmount.currencyCode}
                      </p>
                    </div>
                  </div>
                  );
                })}

                {/* Gifts section */}
                {giftLines.length > 0 && (
                  <div className="space-y-4 md:space-y-3">
                    {/* Centered label between two black lines */}
                    <div className="flex items-center gap-3 my-1">
                      <div className="flex-1 border-t border-black" />
                      <div className="text-black font-bold uppercase tracking-wide text-sm md:text-xs">Regalo GRATIS</div>
                      <div className="flex-1 border-t border-black" />
                    </div>
                    {giftLines.map(({ node: line }) => {
                      const isGift = true;
                      return (
                        <div key={line.id} className="flex gap-4 border-b pb-4 md:pb-3">
                          <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                            <Image
                              src={line.merchandise.product.featuredImage.url}
                              alt={line.merchandise.product.featuredImage.altText || line.merchandise.product.title}
                              fill
                              className="object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-lg md:text-base truncate">
                              {line.merchandise.product.title}
                            </h3>
                            {line.merchandise.title !== 'Default Title' && (
                              <p className="text-base md:text-sm text-gray-500 mt-1">
                                {line.merchandise.title}
                              </p>
                            )}
                            <div className="flex items-center justify-between mt-2">
                              <div />
                              <span className="text-base md:text-sm text-black font-mono">Incluido gratis</span>
                            </div>
                            <p className="text-lg md:text-base font-semibold mt-1">${parseFloat(line.cost.totalAmount.amount).toFixed(2)} {line.cost.totalAmount.currencyCode}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t p-4 space-y-4">
                
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-xl md:text-lg">Total:</span>
                  <span className="font-bold text-3xl md:text-2xl">
                    ${parseFloat(cart.cost.totalAmount.amount).toFixed(2)} {cart.cost.totalAmount.currencyCode}
                  </span>
                </div>

                {showCheckoutForm ? (
                  // Email + Zip capture form
                  <div className="space-y-3">
                    <div className="text-xl md:text-sm font-medium text-gray-700">
                      Información para el checkout:
                    </div>
                    
                    <input
                      type="email"
                      placeholder="tu@email.com *"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 md:px-3 md:py-2 border border-gray-300 rounded-lg text-xl md:text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
                      disabled={isCheckingOut}
                      required
                    />
                    
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Código de descuento"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="flex-1 px-4 py-3 md:px-3 md:py-2 border border-gray-300 rounded-lg text-xl md:text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
                        disabled={isCheckingOut}
                      />
                      {couponCode.trim() && (
                        <span className="text-xs text-green-600 font-medium whitespace-nowrap">Aplicado al checkout</span>
                      )}
                    </div>

                    <input
                      type="text"
                      placeholder="Código postal (opcional)"
                      value={zip}
                      onChange={(e) => setZip(e.target.value)}
                      className="w-full px-4 py-3 md:px-3 md:py-2 border border-gray-300 rounded-lg text-xl md:text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
                      disabled={isCheckingOut}
                    />

                    {/* Gift card removed: gifts now show as read-only cart items */}
                    {/* Gift image preview modal */}
                    {giftPreviewIndex !== null && giftInfo?.giftDetails?.[giftPreviewIndex] && (
                      <div
                        className="absolute inset-0 z-[60] flex items-center justify-center bg-black/50"
                        onClick={() => setGiftPreviewIndex(null)}
                      >
                        <div
                          className="relative bg-white rounded-lg p-3 shadow-xl w-[92%] max-w-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => setGiftPreviewIndex(null)}
                            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                            aria-label="Cerrar"
                          >
                            ✕
                          </button>
                          <div className="relative w-full aspect-square rounded-md overflow-hidden bg-gray-100">
                            <Image
                              src={giftInfo.giftDetails[giftPreviewIndex].imageUrl}
                              alt={giftInfo.giftDetails[giftPreviewIndex].title || giftInfo.giftDetails[giftPreviewIndex].name}
                              fill
                              className="object-cover"
                            />
                          </div>
                          <div className="mt-3 text-center">
                            <div className="text-base font-medium">{giftInfo.gifts?.[giftPreviewIndex] || giftInfo.giftDetails[giftPreviewIndex].name || giftInfo.giftDetails[giftPreviewIndex].title}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={handleStartCheckout}
                      className="w-full btn btn-primary text-lg md:text-lg cursor-pointer hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={!email.trim() || isCheckingOut}
                    >
                      {isCheckingOut ? (
                        <span className="inline-flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                          </svg>
                          Redirigiendo...
                        </span>
                      ) : (giftInfo ? 'Finalizar Compra' : 'Verificar y Finalizar Compra')}
                    </button>

                    {autoRedirect !== null && autoRedirect > 0 && (
                      <div className="text-center text-xs md:text-sm text-gray-500 mt-1">
                        Redirigiendo al checkout en {autoRedirect}s · <button className="underline" onClick={cancelAutoRedirect}>Cancelar</button>
                      </div>
                    )}

                    {/* Payment methods strip */}
                    <div className="flex flex-col items-center gap-1 mt-2 mb-1 opacity-90">
                      <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
                        {[
                          { src: '/images/cards/visa.svg', alt: 'Visa' },
                          { src: '/images/cards/mc.svg', alt: 'Mastercard' },
                          { src: '/images/cards/amex.svg', alt: 'American Express' },
                          { src: '/images/cards/paypal.svg', alt: 'PayPal' },
                          { src: '/images/cards/apple-pay.svg', alt: 'Apple Pay' },
                          { src: '/images/cards/google-pay.svg', alt: 'Google Pay' },
                          { src: '/images/cards/mercadopago.png', alt: 'Mercado Pago' },
                          { src: '/images/cards/oxxo.svg', alt: 'OXXO' },
                        ].map((logo) => (
                          <Image
                            key={logo.alt}
                            src={logo.src}
                            alt={logo.alt}
                            width={64}
                            height={24}
                            className="h-5 md:h-6 w-auto select-none"
                          />
                        ))}
                      </div>
                      <div className="flex flex-col sm:flex-row items-center gap-1 text-[11px] md:text-[12px] text-neutral-700">
                        <div className="flex items-center gap-2">
                          <span className="whitespace-nowrap">Paga en hasta 12 quincenas con</span>
                          <Image
                            src="/images/cards/kueski-pay-badge-logo.svg"
                            alt="Kueski Pay"
                            width={110}
                            height={28}
                            className="h-5 w-auto select-none"
                          />
                        </div>
                        <span className="sm:ml-1 whitespace-nowrap">sin comisiones</span>
                      </div>
                    </div>

                  </div>
                ) : (
                  // Regular checkout button
                  <>
                    <button
                      onClick={handleCheckout}
                      className="w-full btn btn-primary text-lg md:text-lg cursor-pointer hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={!cart || cart.lines.edges.length === 0 || isCheckingOut}
                    >
                      {isCheckingOut ? (
                        <span className="inline-flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                          </svg>
                          Redirigiendo...
                        </span>
                      ) : 'Finalizar Compra'}
                    </button>
                    <div className="flex flex-col items-center gap-1 mt-2 mb-1 opacity-90">
                      <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
                        {[
                          { src: '/images/cards/visa.svg', alt: 'Visa' },
                          { src: '/images/cards/mc.svg', alt: 'Mastercard' },
                          { src: '/images/cards/amex.svg', alt: 'American Express' },
                          { src: '/images/cards/paypal.svg', alt: 'PayPal' },
                          { src: '/images/cards/apple-pay.svg', alt: 'Apple Pay' },
                          { src: '/images/cards/google-pay.svg', alt: 'Google Pay' },
                          { src: '/images/cards/mercadopago.png', alt: 'Mercado Pago' },
                          { src: '/images/cards/oxxo.svg', alt: 'OXXO' },
                        ].map((logo) => (
                          <Image
                            key={logo.alt}
                            src={logo.src}
                            alt={logo.alt}
                            width={64}
                            height={24}
                            className="h-5 md:h-6 w-auto select-none"
                          />
                        ))}
                      </div>
                      <div className="flex flex-col sm:flex-row items-center gap-1 text-[11px] md:text-[12px] text-neutral-700">
                        <div className="flex items-center gap-2">
                          <span className="whitespace-nowrap">Paga en hasta 12 quincenas con</span>
                          <Image
                            src="/images/cards/kueski-pay-badge-logo.svg"
                            alt="Kueski Pay"
                            width={110}
                            height={28}
                            className="h-5 w-auto select-none"
                          />
                        </div>
                        <span className="sm:ml-1 whitespace-nowrap">sin comisiones</span>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Removed 'Limpiar carrito' option per request */}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
